import React, { useEffect, useId, useRef } from 'react';

interface AlertDialogProps {
  title: string;
  message: string;
  onClose: () => void;
  closeLabel?: string;
}

const AlertDialog: React.FC<AlertDialogProps> = ({ title, message, onClose, closeLabel = 'Close' }) => {
  const titleId = useId();
  const messageId = useId();
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeButtonRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={messageId}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5"
        onClick={event => event.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
              <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M12 9v4m0 4h.01M10.3 3.8 2.7 17a2 2 0 0 0 1.7 3h15.2a2 2 0 0 0 1.7-3L13.7 3.8a2 2 0 0 0-3.4 0Z" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="min-w-0">
              <h2 id={titleId} className="text-lg font-semibold text-gray-900">{title}</h2>
              <p id={messageId} className="mt-2 whitespace-pre-wrap text-sm leading-6 text-gray-600">{message}</p>
            </div>
          </div>
        </div>
        <div className="flex justify-end border-t border-gray-100 bg-gray-50 px-6 py-4">
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-black focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2"
          >
            {closeLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AlertDialog;
